import React, { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import StatusButton from "../../Components/StatusButton";
import CreatedByCell from "../../Components/Custom/CreatedByCell";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { getEnquiryStats, getEnquiries, exportEnquiries, formatApiDate, locationFilterParams, normalizeEnquiryStats } from "../../api/superAdminApi";
import { VM_venueTypes } from "../../Helper/RegistrationHelper";
import { LEAD_SOURCE_OPTIONS } from "../../Util/VMConstants";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminEnquiryDetails from "./SuperAdminEnquiryDetails";

const ALL = { value: "", label: "All" };
const CLIENT_STATUS = [ALL, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];
const RECORD_STATUS = [ALL, { value: "New", label: "New" }, { value: "In Progress", label: "In Progress" }];
const BOOKING_TYPE = [ALL, { value: "Confirmed", label: "Confirmed" }, { value: "Lead", label: "Lead" }, { value: "Not-interested", label: "Not-interested" }];
const VENUE_TYPE_OPTIONS = [ALL, ...VM_venueTypes.map((v) => ({ value: v.value, label: v.label }))];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const FILTER_ITEM = { xs: 12, sm: 6, md: 4, lg: 3 };
const filterFormControlSx = { minWidth: { xs: "100%", sm: 220 }, width: "100%" };
const filterMenuProps = {
  PaperProps: { sx: { maxHeight: 320, minWidth: 240, mt: 0.5 } },
};

function FilterSelect({ label, labelId, value, onChange, options }) {
  return (
    <FormControl fullWidth size="small" sx={filterFormControlSx}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        displayEmpty
        notched
        MenuProps={filterMenuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value || "all"} value={o.value}>
            {o.value ? o.label : <em>{o.label}</em>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FilterSectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

/** Case-insensitive match on customer name, mobile, and email. */

export default function VMSuperAdminRecords() {
  const { adminScope, locationLocked } = useAdminPanel();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [clientStatus, setClientStatus] = useState("");
  const [recordStatus, setRecordStatus] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [venueType, setVenueType] = useState("");
  const [leadSource, setLeadSource] = useState("");
  const [enquiryStats, setEnquiryStats] = useState({});
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);

  const loc = useLocationCascading();

  const buildFilters = useCallback(
    (pageOverride) => {
      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: rowsPerPage,
        ...locationFilterParams(loc, adminScope, { locked: locationLocked }),
      };
      const from = formatApiDate(fromDate);
      const to = formatApiDate(toDate);
      if (from) params.from = from;
      if (to) params.to = to;
      if (searchKey.trim()) params.search = searchKey.trim();
      if (recordStatus) params.record_status = recordStatus;
      if (bookingType) params.appointment_status = bookingType;
      if (venueType) params.venue_type = venueType;
      if (leadSource) params.lead = leadSource;
      return params;
    },
    [page, rowsPerPage, fromDate, toDate, searchKey, recordStatus, bookingType, venueType, leadSource, loc, adminScope, locationLocked]
  );

  const fetchEnquiryStats = useCallback(async () => {
    try {
      const res = await getEnquiryStats(buildFilters());
      setEnquiryStats(normalizeEnquiryStats(res.data?.data ?? {}));
    } catch {
      setEnquiryStats({});
    }
  }, [buildFilters]);

  const fetchRecords = useCallback(
    async (pageOverride) => {
      setLoading(true);
      const filters = buildFilters(pageOverride);
      try {
        const res = await getEnquiries(filters);
        const body = res.data;
        if (body?.status_code === 200) {
          setRows(body.data || []);
          setTotalRecords(body.totalCount ?? 0);
        } else {
          setRows([]);
          setTotalRecords(0);
        }
      } catch {
        setRows([]);
        setTotalRecords(0);
        toast.info("Enquiries API unavailable.");
      } finally {
        setLoading(false);
      }
    },
    [buildFilters]
  );

  useEffect(() => {
    fetchRecords();
    fetchEnquiryStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    setFiltersOpen(false);
    setPage(0);
    fetchEnquiryStats();
    fetchRecords(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchRecords(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const next = parseInt(event.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    fetchRecords(0);
  };

  const exportData = async () => {
    try {
      await exportEnquiries(buildFilters());
    } catch {
      toast.warning("Export failed.");
    }
  };

  const getRowEnquiryId = (row) => row.enquiry_id ?? row.appointment_id ?? row.id;

  const openEnquiryDetail = (row) => {
    const enquiryId = getRowEnquiryId(row);
    if (!enquiryId) {
      toast.info("Enquiry ID not available for this row.");
      return;
    }
    setSelectedEnquiryId(String(enquiryId));
    setDetailOpen(true);
  };

  const closeEnquiryDetail = () => {
    setDetailOpen(false);
    setSelectedEnquiryId(null);
  };

  return (
    <Box>
      <SuperAdminPageHeader
        title="Enquiries"
        actions={
          <Button
            variant={filtersOpen ? "contained" : "outlined"}
            color="primary"
            size="medium"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen(true)}
            aria-expanded={filtersOpen}
            aria-controls="records-filters-panel"
          >
            Filters
          </Button>
        }
      />

      {filtersOpen ? (
        <Paper id="records-filters-panel" variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2.5}>
            <FilterSectionTitle>Registration date</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Created from"
                  value={fromDate}
                  onChange={setFromDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Created to"
                  value={toDate}
                  onChange={setToDate}
                  format="DD/MM/YYYY"
                  minDate={fromDate?.isValid() ? fromDate : undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Location</FilterSectionTitle>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects
                {...loc}
                locationLocked={locationLocked}
                lockedScope={adminScope}
                menuMinWidth={280}
                itemProps={FILTER_ITEM}
              />
            </Grid>

            <Divider />

            <FilterSectionTitle>Record filters</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="records-filter-client-status"
                  label="Client status"
                  value={clientStatus}
                  onChange={(e) => setClientStatus(e.target.value)}
                  options={CLIENT_STATUS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="records-filter-record-status"
                  label="Record status"
                  value={recordStatus}
                  onChange={(e) => setRecordStatus(e.target.value)}
                  options={RECORD_STATUS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="records-filter-booking-type"
                  label="Booking type"
                  value={bookingType}
                  onChange={(e) => setBookingType(e.target.value)}
                  options={BOOKING_TYPE}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="records-filter-venue-type"
                  label="Venue type"
                  value={venueType}
                  onChange={(e) => setVenueType(e.target.value)}
                  options={VENUE_TYPE_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="records-filter-lead-source"
                  label="Lead source"
                  value={leadSource}
                  onChange={(e) => setLeadSource(e.target.value)}
                  options={[ALL, ...LEAD_SOURCE_OPTIONS.map((option) => ({ value: option, label: option }))]}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Search</FilterSectionTitle>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} lg={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Name, mobile, or email"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Button variant="contained" onClick={handleSearchClick} disabled={loading} fullWidth>
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Button variant="outlined" onClick={exportData} disabled={loading} fullWidth>
                  Export Data
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: "Total", value: enquiryStats.total },
          { label: "Lead", value: enquiryStats.lead },
          { label: "Confirmed", value: enquiryStats.confirmed },
          { label: "Not-interested", value: enquiryStats.notInterested },
        ].map((item) => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.main", color: "primary.contrastText" }}>
              <Typography variant="body2">{item.label}</Typography>
              <Typography variant="statValue" sx={{ fontWeight: 700 }}>{item.value ?? 0}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {["S.No", "Enquiry ID", "Name", "Phone", "Event Date", "Sub Venue", "Lead Source", "Status", "Created By"].map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} aria-label="Loading enquiries" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No records for the current criteria. Open Filters, adjust options, then Search.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const enquiryId = getRowEnquiryId(row);
                const eventDates = Array.isArray(row.appointment_date)
                  ? row.appointment_date.map((dateStr) => dayjs(dateStr).format("DD/MM/YYYY")).join(", ")
                  : "";
                const subVenueNames = Array.isArray(row.event_sub_venue_names) ? row.event_sub_venue_names.filter(Boolean) : [];
                const subVenueTypes = Array.isArray(row.event_sub_venue_types) ? row.event_sub_venue_types.filter(Boolean) : [];
                const subVenue = subVenueNames.length
                  ? subVenueNames.map((name, index) => (subVenueTypes[index] ? `${name} (${subVenueTypes[index]})` : name)).join(", ")
                  : "";
                const phone = `${row.country_code || ""} ${row.mobile ?? row.phone ?? ""}`.trim();
                const createdBy = row.created_by || row.createdBy || null;
                return (
                <TableRow key={row.id ?? idx} sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => openEnquiryDetail(row)}
                      sx={{ color: "primary.main", fontWeight: 700, textTransform: "none", px: 0.5, minWidth: 0 }}
                    >
                      {enquiryId || "—"}
                    </Button>
                  </TableCell>
                  <TableCell>{row.customer_name ?? row.name ?? "—"}</TableCell>
                  <TableCell>{phone || "—"}</TableCell>
                  <TableCell>{eventDates || "—"}</TableCell>
                  <TableCell>{subVenue || "—"}</TableCell>
                  <TableCell>{row.lead || "—"}</TableCell>
                  <TableCell>
                    <StatusButton status={row.enquiry_status ?? row.appointment_status} justify="left" />
                  </TableCell>
                  <TableCell>
                    {createdBy && typeof createdBy === "object" ? (
                      <CreatedByCell user={createdBy} />
                    ) : (
                      createdBy || "—"
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                disabled={loading}
                slotProps={{
                  select: {
                    "aria-label": "Rows per page",
                    sx: { minWidth: 72 },
                  },
                }}
                sx={{ border: 0 }}
                colSpan={9}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Dialog open={detailOpen} onClose={closeEnquiryDetail} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1.5,
          }}
        >
          Enquiry {selectedEnquiryId}
          <IconButton onClick={closeEnquiryDetail} aria-label="Close" size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEnquiryId ? <SuperAdminEnquiryDetails enquiryId={selectedEnquiryId} /> : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
